import React, { FunctionComponent } from 'react';
import { omit } from 'underscore';

import { truncate } from '@trello/strings';
import { deserializeJSONString } from '@trello/graphql';
import { avatarsFromAvatarUrl } from '@trello/members';
import { EntityTransformers } from 'app/src/components/ActionEntities/EntityTransformers';
import { RouterLink } from 'app/src/components/RouterLink';
import { markdown } from 'app/src/components/TrelloMarkdown';
// eslint-disable-next-line no-restricted-imports
import { ActionDataModel } from 'app/gamma/src/types/models';
import {
  ActionDisplayType,
  ActionEntitiesObject,
} from 'app/src/components/ActionEntities';
import { useTrelloActionQuery } from './TrelloActionQuery.generated';

const isCommentLike = ({ type }: { type?: string }): boolean => {
  if (!type) {
    return false;
  }

  return type === 'commentCard' || type === 'copyCommentCard';
};

import { KnownService, KnownServiceComponentProps } from './KnownService';

// eslint-disable-next-line @trello/less-matches-component
import styles from './KnownService.less';

interface OwnProps extends KnownServiceComponentProps {
  fullUrl: string;
  pathname: string;
  shortLink?: string;
  idAction: string;
}

interface TrelloActionComponentUnconnectedProps extends OwnProps {
  action?: {
    data?: ActionDataModel;
    display: ActionDisplayType;
    id: string;
    memberCreator: {
      avatarUrl?: string | null;
    };
    type: string;
  };
}

export const TrelloActionComponentUnconnected = (
  props: TrelloActionComponentUnconnectedProps,
) => {
  const getIcon = () => {
    const { action } = props;
    const memberCreator = action ? action.memberCreator : null;

    if (!memberCreator?.avatarUrl) {
      return (
        <img
          className={styles.knownServiceIcon}
          alt="Trello Icon"
          src={require('resources/images/services/trello.png')}
        />
      );
    }

    return (
      <img
        className={styles.knownServiceIcon}
        alt="Trello Icon"
        src={avatarsFromAvatarUrl(memberCreator.avatarUrl)['30']}
      />
    );
  };

  const getText = () => {
    const { action } = props;

    if (!action || !action.display) {
      return '';
    }

    const idContext =
      action.data && action.data.card ? action.data.card.id : '';

    const entityTransformer = new EntityTransformers(action.display)
      .fixTranslatebleLocaleGroup('actions')
      .addUrlContext()
      .makeEntitiesFriendly();

    let displayText = '';
    if (isCommentLike(action)) {
      const entities = entityTransformer.value().entities;

      if (entities) {
        const memberCreator =
          entities.memberCreator.type === 'member'
            ? entities.memberCreator
            : null;
        const comment =
          entities.comment.type === 'comment' ? entities.comment : null;

        if (comment && memberCreator) {
          displayText = `${memberCreator.text}: ${
            markdown ? markdown.comments.textInline(comment.text) : comment.text
          }`;
        }
      }
    } else {
      displayText = entityTransformer.getEntityStrings(idContext, 'actions');
    }

    return truncate(displayText, 64);
  };

  const { action, fullUrl, pathname, shortLink, ...rest } = props;
  const anchorProps = omit(rest, ['loadAction', 'idAction']);

  return (
    <RouterLink
      {...anchorProps}
      href={pathname}
      className={styles.knownServiceLink}
      target=""
      rel=""
    >
      {getIcon()}
      {getText()}
    </RouterLink>
  );
};

const TrelloActionComponent: FunctionComponent<OwnProps> = ({
  shortLink,
  fullUrl,
  pathname,
  idAction,
}) => {
  const { data } = useTrelloActionQuery({
    variables: {
      id: idAction,
    },
  });
  if (!data?.action) {
    return null;
  }
  const action = {
    ...data.action,
    data: deserializeJSONString<ActionDataModel>(data.action.data) ?? undefined,
    display: {
      translationKey: data.action.display.translationKey || '',
      entities:
        deserializeJSONString<ActionEntitiesObject>(
          data.action.display.entities,
        ) ?? undefined,
    },
  };

  return (
    <TrelloActionComponentUnconnected
      fullUrl={fullUrl}
      shortLink={shortLink}
      pathname={pathname}
      action={action}
      idAction={idAction}
    />
  );
};

export const TrelloAction: KnownService<OwnProps> = {
  match: {
    protocol: location.protocol,
    host: location.host,
    pathname: new RegExp(`\
^\
/c/\
(\
[a-zA-Z0-9]{8}|\
[a-fA-F0-9]{24}\
)\
(?:$|/.*)\
`),
    hash: new RegExp(`\
^\
\\#(?:comment|action)-([0-9a-f]{24})\
$\
`),
  },
  getMatchProps: ([fullUrl, pathname, shortLink, idAction]: string[]) => {
    return { fullUrl, pathname, shortLink, idAction };
  },
  Component: TrelloActionComponent,
};
